import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Shuffle, Copy } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface ConversionUnit {
  name: string;
  symbol: string;
  factor: number; // Factor to convert to base unit
}

interface ConversionCategory {
  name: string;
  baseUnit: string;
  units: ConversionUnit[];
}

const conversionCategories: ConversionCategory[] = [
  {
    name: 'Length',
    baseUnit: 'meter',
    units: [
      { name: 'Millimeter', symbol: 'mm', factor: 0.001 },
      { name: 'Centimeter', symbol: 'cm', factor: 0.01 },
      { name: 'Meter', symbol: 'm', factor: 1 },
      { name: 'Kilometer', symbol: 'km', factor: 1000 },
      { name: 'Inch', symbol: 'in', factor: 0.0254 },
      { name: 'Foot', symbol: 'ft', factor: 0.3048 },
      { name: 'Yard', symbol: 'yd', factor: 0.9144 },
      { name: 'Mile', symbol: 'mi', factor: 1609.344 },
    ]
  },
  {
    name: 'Weight',
    baseUnit: 'gram',
    units: [
      { name: 'Milligram', symbol: 'mg', factor: 0.001 },
      { name: 'Gram', symbol: 'g', factor: 1 },
      { name: 'Kilogram', symbol: 'kg', factor: 1000 },
      { name: 'Ounce', symbol: 'oz', factor: 28.3495 },
      { name: 'Pound', symbol: 'lb', factor: 453.592 },
      { name: 'Stone', symbol: 'st', factor: 6350.29 },
      { name: 'Ton', symbol: 't', factor: 1000000 },
    ]
  },
  {
    name: 'Temperature',
    baseUnit: 'celsius',
    units: [
      { name: 'Celsius', symbol: '°C', factor: 1 },
      { name: 'Fahrenheit', symbol: '°F', factor: 1 },
      { name: 'Kelvin', symbol: 'K', factor: 1 },
    ]
  },
  {
    name: 'Volume',
    baseUnit: 'liter',
    units: [
      { name: 'Milliliter', symbol: 'ml', factor: 0.001 },
      { name: 'Liter', symbol: 'l', factor: 1 },
      { name: 'Gallon (US)', symbol: 'gal', factor: 3.78541 },
      { name: 'Gallon (UK)', symbol: 'gal', factor: 4.54609 },
      { name: 'Fluid Ounce (US)', symbol: 'fl oz', factor: 0.0295735 },
      { name: 'Cup', symbol: 'cup', factor: 0.236588 },
      { name: 'Pint', symbol: 'pt', factor: 0.473176 },
      { name: 'Quart', symbol: 'qt', factor: 0.946353 },
    ]
  },
  {
    name: 'Area',
    baseUnit: 'square meter',
    units: [
      { name: 'Square Millimeter', symbol: 'mm²', factor: 0.000001 },
      { name: 'Square Centimeter', symbol: 'cm²', factor: 0.0001 },
      { name: 'Square Meter', symbol: 'm²', factor: 1 },
      { name: 'Square Kilometer', symbol: 'km²', factor: 1000000 },
      { name: 'Square Inch', symbol: 'in²', factor: 0.00064516 },
      { name: 'Square Foot', symbol: 'ft²', factor: 0.092903 },
      { name: 'Square Yard', symbol: 'yd²', factor: 0.836127 },
      { name: 'Acre', symbol: 'ac', factor: 4046.86 },
      { name: 'Hectare', symbol: 'ha', factor: 10000 },
    ]
  }
];

export const UnitConverterTool: React.FC = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState(conversionCategories[0]);
  const [fromUnit, setFromUnit] = useState(selectedCategory.units[0]);
  const [toUnit, setToUnit] = useState(selectedCategory.units[1]);
  const [inputValue, setInputValue] = useState('1');
  const [outputValue, setOutputValue] = useState('');

  const convertTemperature = (value: number, from: string, to: string): number => {
    // Convert to Celsius first
    let celsius = value;
    if (from === '°F') {
      celsius = (value - 32) * 5/9;
    } else if (from === 'K') {
      celsius = value - 273.15;
    }

    // Convert from Celsius to target
    if (to === '°F') {
      return celsius * 9/5 + 32;
    } else if (to === 'K') {
      return celsius + 273.15;
    }
    return celsius;
  };

  const convertValue = (value: number, fromUnit: ConversionUnit, toUnit: ConversionUnit, category: ConversionCategory): number => {
    if (category.name === 'Temperature') {
      return convertTemperature(value, fromUnit.symbol, toUnit.symbol);
    }
    
    // Convert to base unit, then to target unit
    const baseValue = value * fromUnit.factor;
    return baseValue / toUnit.factor;
  };

  useEffect(() => {
    const numValue = parseFloat(inputValue);
    if (!isNaN(numValue)) {
      const result = convertValue(numValue, fromUnit, toUnit, selectedCategory);
      setOutputValue(result.toString());
    } else {
      setOutputValue('');
    }
  }, [inputValue, fromUnit, toUnit, selectedCategory]);

  useEffect(() => {
    setFromUnit(selectedCategory.units[0]);
    setToUnit(selectedCategory.units[1] || selectedCategory.units[0]);
  }, [selectedCategory]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const swapUnits = () => {
    const temp = fromUnit;
    setFromUnit(toUnit);
    setToUnit(temp);
    setInputValue(outputValue);
  };

  return (
    <div className="space-y-6">
      {/* Category Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shuffle className="w-5 h-5" />
            <span>{t('tools.unitConverter.unitConverter')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.unitConverter.category')}
              </label>
              <select
                value={selectedCategory.name}
                onChange={(e) => {
                  const category = conversionCategories.find(cat => cat.name === e.target.value);
                  if (category) setSelectedCategory(category);
                }}
                className="input"
              >
                {conversionCategories.map((category) => (
                  <option key={category.name} value={category.name}>
                    {t(`tools.unitConverter.categories.${category.name.toLowerCase()}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conversion */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.unitConverter.convert')} {t(`tools.unitConverter.categories.${selectedCategory.name.toLowerCase()}`)}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* From */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tools.unitConverter.from')}
                </label>
                <select
                  value={fromUnit.symbol}
                  onChange={(e) => {
                    const unit = selectedCategory.units.find(u => u.symbol === e.target.value);
                    if (unit) setFromUnit(unit);
                  }}
                  className="input mb-2"
                >
                  {selectedCategory.units.map((unit) => (
                    <option key={unit.symbol} value={unit.symbol}>
                      {unit.name} ({unit.symbol})
                    </option>
                  ))}
                </select>
                <Input
                  type="number"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Enter value"
                  className="font-mono"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tools.unitConverter.to')}
                </label>
                <select
                  value={toUnit.symbol}
                  onChange={(e) => {
                    const unit = selectedCategory.units.find(u => u.symbol === e.target.value);
                    if (unit) setToUnit(unit);
                  }}
                  className="input mb-2"
                >
                  {selectedCategory.units.map((unit) => (
                    <option key={unit.symbol} value={unit.symbol}>
                      {unit.name} ({unit.symbol})
                    </option>
                  ))}
                </select>
                <div className="relative">
                  <Input
                    type="text"
                    value={outputValue}
                    readOnly
                    placeholder="Result"
                    className="font-mono bg-gray-50 dark:bg-gray-800"
                  />
                  {outputValue && (
                    <Button
                      onClick={() => copyToClipboard(outputValue)}
                      size="sm"
                      variant="ghost"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Swap Button */}
            <div className="flex justify-center">
              <Button
                onClick={swapUnits}
                variant="outline"
                size="sm"
              >
                <Shuffle className="w-4 h-4 mr-2" />
                {t('tools.unitConverter.swapUnits')}
              </Button>
            </div>

            {/* Result Display */}
            {inputValue && outputValue && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="text-center">
                  <span className="text-lg font-mono">
                    {inputValue} {fromUnit.symbol} = {parseFloat(outputValue).toLocaleString()} {toUnit.symbol}
                  </span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Conversions */}
      {inputValue && outputValue && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.unitConverter.quickConversions')} {inputValue} {fromUnit.symbol}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {selectedCategory.units
                .filter(unit => unit.symbol !== fromUnit.symbol)
                .map((unit) => {
                  const result = convertValue(parseFloat(inputValue), fromUnit, unit, selectedCategory);
                  return (
                    <div
                      key={unit.symbol}
                      className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <span className="text-sm font-medium">{unit.name}:</span>
                      <div className="flex items-center space-x-2">
                        <span className="font-mono text-sm">
                          {result.toLocaleString()} {unit.symbol}
                        </span>
                        <Button
                          onClick={() => copyToClipboard(result.toString())}
                          size="sm"
                          variant="ghost"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.unitConverter.aboutUnitConversion')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.unitConverter.supportedCategories')}</h4>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>{t('tools.unitConverter.categories.length')}:</strong> {t('tools.unitConverter.lengthDescription')}</li>
                <li><strong>{t('tools.unitConverter.categories.weight')}:</strong> {t('tools.unitConverter.weightDescription')}</li>
                <li><strong>{t('tools.unitConverter.categories.temperature')}:</strong> {t('tools.unitConverter.temperatureDescription')}</li>
                <li><strong>{t('tools.unitConverter.categories.volume')}:</strong> {t('tools.unitConverter.volumeDescription')}</li>
                <li><strong>{t('tools.unitConverter.categories.area')}:</strong> {t('tools.unitConverter.areaDescription')}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.unitConverter.accuracy')}</h4>
              <p>
                {t('tools.unitConverter.accuracyDescription')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
